import { useState } from "react";
import type { SaleLine } from "../../api/types";
import { lineCostIsKnown } from "../../utils/salesTotals";
import { costOrUnknown, partlyKnownCost, sumKnownCosts } from "../../utils/unknownCost";
import { Modal } from "../Modal";

function eur(n: string | number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  return Number.isFinite(v) ? `€${v.toFixed(2)}` : "—";
}

function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

interface Props {
  line: SaleLine;
}

export function SaleBouquetLineDetail({ line }: Props) {
  const [open, setOpen] = useState(false);
  // G-151: an undone sale's line released its bouquet but still names it.
  const bb = line.bouquetBuild ?? line.voidedBouquetBuild;
  if (line.kind !== "BOUQUET" || !bb) return null;

  const title = bb.template?.name ?? "Custom bouquet";
  const lines = bb.lines ?? [];
  // WO-B (OD-198): a bouquet with any component of unknown cost is one unknown
  // unit on its line; its costs below are then the known part only.
  const costIncomplete = !lineCostIsKnown(line);

  return (
    <>
      <button
        type="button"
        className="button plain"
        onClick={() => setOpen(true)}
        style={{
          textAlign: "left",
          width: "100%",
          justifyContent: "flex-start",
          padding: "var(--space-2) var(--space-2)",
          borderRadius: "var(--radius-md)",
          fontWeight: 500,
        }}
      >
        <span className="badge primary" style={{ marginRight: 8 }}>
          Bouquet
        </span>
        {title}
        <span className="muted" style={{ marginLeft: 8, fontSize: 12, fontWeight: 400 }}>
          View composition →
        </span>
      </button>

      {open ? (
        <Modal title={title} onClose={() => setOpen(false)}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "var(--space-3)",
              }}
            >
              <div>
                <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
                  Ticket sale price
                </div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{eur(line.unitPriceEur)}</div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
                  Revenue (line)
                </div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{eur(line.lineTotalEur)}</div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
                  Cost of goods (line)
                </div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>
                  {partlyKnownCost(line.lineCogsEur, costIncomplete, eur)}
                </div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
                  Cost to build
                </div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>
                  {partlyKnownCost(bb.totalCogsEur, costIncomplete, eur)}
                </div>
              </div>
            </div>

            <div
              style={{
                padding: "var(--space-3)",
                borderRadius: "var(--radius-md)",
                background: "var(--color-surface-muted)",
              }}
            >
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                Built {fmtDate(bb.createdAt)} · {bb.zone.name} ({bb.zone.zoneType}) ·{" "}
                {bb.builtBy.fullName}
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                Build ID <code style={{ fontSize: 11 }}>{bb.id}</code>
              </div>
            </div>

            {lines.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                No flower lines were returned for this build. Try refreshing the page.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
                {lines.map((bl) => {
                  // A slice from a lot of unknown cost has no cost — never €0.
                  const lineCogs = sumKnownCosts(bl.allocations.map((x) => x.lineCostEur));
                  return (
                    <div key={bl.id}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          flexWrap: "wrap",
                          gap: "var(--space-2)",
                          fontWeight: 600,
                          marginBottom: "var(--space-2)",
                        }}
                      >
                        <span>
                          {bl.item.name}{" "}
                          <span className="muted" style={{ fontWeight: 400 }}>
                            · {Number(bl.quantity)} {bl.uom}
                          </span>
                        </span>
                        <span className="muted" style={{ fontWeight: 500 }}>
                          Cost of goods{" "}
                          {partlyKnownCost(lineCogs.knownEur, lineCogs.unknownCount > 0, eur)}
                        </span>
                      </div>
                      {bl.allocations.length === 0 ? (
                        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                          No batch allocations recorded.
                        </p>
                      ) : (
                        <div className="table-scroll" style={{ borderRadius: "var(--radius-md)" }}>
                          <table style={{ fontSize: 13 }}>
                            <thead>
                              <tr>
                                <th>Batch</th>
                                <th style={{ textAlign: "right" }}>Qty</th>
                                <th style={{ textAlign: "right" }}>Unit cost</th>
                                <th style={{ textAlign: "right" }}>Line cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bl.allocations.map((al) => (
                                <tr key={al.id}>
                                  <td>
                                    <code style={{ fontSize: 12 }}>{al.batch.batchCode}</code>
                                  </td>
                                  <td style={{ textAlign: "right" }}>{Number(al.qty)}</td>
                                  <td style={{ textAlign: "right" }}>{costOrUnknown(al.unitCostEur, eur)}</td>
                                  <td style={{ textAlign: "right" }}>{costOrUnknown(al.lineCostEur, eur)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Modal>
      ) : null}
    </>
  );
}
